/**
 * Undo/redo history for structural step operations in the sequence builder
 * Build step (FIR-7544).
 *
 * Scope: structural operations only (add, duplicate, remove a step; add or
 * remove an A/B split variant). Text edits in subject/body/wait inputs keep
 * the browser's or Quill's native undo while those fields are focused, so the
 * app-level stack never fights the native one. Each entry records the
 * operation kind, the index it happened at, and the step content needed to
 * reverse it.
 *
 * Variants need no special handling here: rows live in a flat array with
 * variants adjacent to their family original, every recorded operation is a
 * single-row insert/remove (the builder blocks deleting an original that
 * still has variants), and the captured step content carries the variant
 * fields (`is_variant`, `variant_from_step_id`). LIFO discipline guarantees
 * the array at undo time equals the array right after the recorded operation,
 * so restoring at the recorded index always rebuilds family adjacency. The
 * variant pause/resume toggle is intentionally NOT recorded — it is a live
 * server mutation with its own inverse control, not a draft edit.
 *
 * The core is pure: callers apply the returned `StepRestoreAction` to their
 * form state (react-hook-form field array in WizardSequenceStep) and store
 * the returned history state. `getCurrentStep` lets the core refresh an
 * entry's step content right before a removal, so content typed after the
 * original operation survives an undo/redo round trip.
 */

import type { SequenceStepForm } from '../SequenceTab/SequenceStepFormItem'

/** Maximum number of undoable operations kept in the session history. */
export const MAX_HISTORY_ENTRIES = 50

export interface StepHistoryEntry {
  /** The operation the user performed (not its inverse). */
  kind: 'add' | 'remove'
  /** Index in the steps array where the operation happened. */
  index: number
  /** Step content at the moment it was last removed from the form. */
  step: SequenceStepForm
}

export interface StepHistoryState {
  past: StepHistoryEntry[]
  future: StepHistoryEntry[]
}

export type StepRestoreAction =
  | {
      type: 'insert'
      index: number
      /** The step content to restore at `index`. */
      step: SequenceStepForm
    }
  | { type: 'remove'; index: number }

export const EMPTY_STEP_HISTORY: StepHistoryState = { past: [], future: [] }

export function canUndo(state: StepHistoryState): boolean {
  return state.past.length > 0
}

export function canRedo(state: StepHistoryState): boolean {
  return state.future.length > 0
}

/**
 * Guard for UI affordances (e.g. the delete toast's Undo button) that promise
 * to reverse one specific operation. The caller snapshots `past.length` right
 * after recording that operation; the promised operation is still the next
 * one undo would reverse only while the stack depth matches that snapshot.
 * Any later structural op, undo, or redo changes the depth, at which point
 * triggering a generic LIFO undo would reverse the wrong operation.
 */
export function canUndoSnapshot(
  state: StepHistoryState,
  snapshotPastLength: number
): boolean {
  return snapshotPastLength > 0 && state.past.length === snapshotPastLength
}

/**
 * Record a user-performed structural operation. Clears the redo stack
 * (standard branch truncation) and caps history at MAX_HISTORY_ENTRIES.
 */
export function pushEntry(
  state: StepHistoryState,
  entry: StepHistoryEntry
): StepHistoryState {
  const past = [...state.past, entry]
  return {
    past: past.slice(-MAX_HISTORY_ENTRIES),
    future: [],
  }
}

/**
 * Refresh an entry's captured step content from the live form right before
 * the step is removed, so the opposite operation restores any edits made
 * since the entry was recorded.
 */
function withCurrentContent(
  entry: StepHistoryEntry,
  getCurrentStep: (index: number) => SequenceStepForm | undefined
): StepHistoryEntry {
  return { ...entry, step: getCurrentStep(entry.index) ?? entry.step }
}

export interface StepHistoryTransition {
  state: StepHistoryState
  action: StepRestoreAction
}

/**
 * Undo the most recent operation.
 *
 * Undoing a `remove` re-inserts the captured step. Undoing an `add` removes
 * the step, first refreshing the stored content via `getCurrentStep` so a
 * redo restores anything typed into the step since it was added.
 */
export function undoStep(
  state: StepHistoryState,
  getCurrentStep: (index: number) => SequenceStepForm | undefined
): StepHistoryTransition | null {
  const entry = state.past[state.past.length - 1]
  if (!entry) return null
  const past = state.past.slice(0, -1)

  if (entry.kind === 'remove') {
    return {
      state: { past, future: [...state.future, entry] },
      action: { type: 'insert', index: entry.index, step: entry.step },
    }
  }

  return {
    state: {
      past,
      future: [...state.future, withCurrentContent(entry, getCurrentStep)],
    },
    action: { type: 'remove', index: entry.index },
  }
}

/**
 * Redo the most recently undone operation. Mirrors `undoStep`: re-applying a
 * `remove` refreshes the stored content first so the next undo restores any
 * edits made while the step was back in the form.
 */
export function redoStep(
  state: StepHistoryState,
  getCurrentStep: (index: number) => SequenceStepForm | undefined
): StepHistoryTransition | null {
  const entry = state.future[state.future.length - 1]
  if (!entry) return null
  const future = state.future.slice(0, -1)

  if (entry.kind === 'add') {
    return {
      state: { past: [...state.past, entry], future },
      action: { type: 'insert', index: entry.index, step: entry.step },
    }
  }

  return {
    state: {
      past: [...state.past, withCurrentContent(entry, getCurrentStep)],
      future,
    },
    action: { type: 'remove', index: entry.index },
  }
}

/**
 * Drop Bison step ids that the server no longer knows about.
 *
 * An undone deletion can resurrect a step whose id was already deleted from
 * EmailBison by a previous autosave. Re-sending that id would route it through
 * Bison's update-existing-steps PUT, which rejects unknown ids and fails the
 * whole save. Stripping the stale id makes the restored step a "new" step,
 * exactly like a duplicated step, so the existing create path persists it.
 */
export function dropStaleStepIds<T extends { id?: number }>(
  steps: T[],
  knownIds: ReadonlySet<number>
): T[] {
  return steps.map((step) =>
    step.id !== undefined && !knownIds.has(step.id)
      ? { ...step, id: undefined }
      : step
  )
}

/**
 * True when a keyboard event originated inside an editable element (text
 * input, textarea, select, or contenteditable rich-text editor). Undo/redo
 * shortcuts must not fire there: those surfaces own their native undo stacks.
 */
export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  // `closest` covers descendants of a contenteditable root (Quill dispatches
  // key events from nested nodes); `isContentEditable` covers inheritance
  // cases the attribute selector misses.
  if (
    target.isContentEditable ||
    target.closest('[contenteditable="true"]') !== null
  ) {
    return true
  }
  const tag = target.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
}

export type UndoRedoIntent = 'undo' | 'redo'

/**
 * Map a keydown to an undo/redo intent following platform conventions:
 * Cmd+Z / Shift+Cmd+Z on macOS, Ctrl+Z / Shift+Ctrl+Z and Ctrl+Y on
 * Windows/Linux. Returns null for anything else.
 */
export function getUndoRedoIntent(event: {
  key: string
  metaKey: boolean
  ctrlKey: boolean
  shiftKey: boolean
  altKey: boolean
}): UndoRedoIntent | null {
  const modifier = event.metaKey || event.ctrlKey
  if (!modifier || event.altKey) return null
  const key = event.key.toLowerCase()
  if (key === 'z') return event.shiftKey ? 'redo' : 'undo'
  if (key === 'y' && event.ctrlKey && !event.metaKey && !event.shiftKey) {
    return 'redo'
  }
  return null
}
